import {
  Component,
  Input,
  OnChanges,
  SimpleChanges,
} from '@angular/core';
import {
  AbstractControl,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ValidationErrors,
} from '@angular/forms';
import { CustomSelectOption } from 'src/app/core/interfaces/custom-select-option';
import { Stack } from 'src/app/core/models/stack';
import { StackService } from 'src/app/core/services/general/stack.service';
import { PaginationOptions } from 'src/app/core/models/pagination_options';

@Component({
  selector: 'app-stack-input',
  templateUrl: './stack-input.component.html',
  styleUrls: ['./stack-input.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: StackInputComponent,
    },
    {
      provide: NG_VALIDATORS,
      multi: true,
      useExisting: StackInputComponent,
    },
  ],
})
export class StackInputComponent implements OnChanges {
  @Input()
  title = "Select Stack...";

  @Input()
  isDisabled = false;


  @Input()
  filterCommodityUuid: string | null = null;

  @Input()
  filterWarehouseUuid: string | null = null;

  public value?: string;
  public localValue?: CustomSelectOption;

  public stacksList: Array<CustomSelectOption> = [];
  public stacks: Array<Stack> = [];
  public loading = false;


  constructor(
    private stackService: StackService,
  ) {
    // Don't load stacks in constructor anymore
    // They will be loaded when filters are provided
  }

  ngOnInit(): void {
    // Load stacks if filters are already available
    if (this.filterCommodityUuid || this.filterWarehouseUuid) {
      this.loadStacksForFilters(this.filterCommodityUuid || '', this.filterWarehouseUuid || '');
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    // React to changes in filterCommodityUuid or filterWarehouseUuid
    if (changes['filterCommodityUuid'] || changes['filterWarehouseUuid']) {
      const commodityChanged = changes['filterCommodityUuid'];
      const warehouseChanged = changes['filterWarehouseUuid'];

      const currentCommodity = commodityChanged?.currentValue ?? this.filterCommodityUuid;
      const previousCommodity = commodityChanged?.previousValue;
      const currentWarehouse = warehouseChanged?.currentValue ?? this.filterWarehouseUuid;
      const previousWarehouse = warehouseChanged?.previousValue;

      // If either filter changed, clear current selection and load new stacks
      if (currentCommodity !== previousCommodity || currentWarehouse !== previousWarehouse) {
        this.clearSelection();

        if (currentCommodity || currentWarehouse) {
          this.loadStacksForFilters(currentCommodity || '', currentWarehouse || '');
        } else {
          // Clear stacks if no filters selected
          this.clearStacks();
        }
      }
    }
  }

  private loadStacksForFilters(commodityUuid: string, warehouseUuid: string): void {
    if (!commodityUuid && !warehouseUuid) {
      this.clearStacks();
      return;
    }

    this.loading = true;
    // With a commodity, filter by commodity + warehouse. WITHOUT a commodity (e.g. the repack
    // destination stack, where the commodity is derived from the source transaction and not on
    // the form), fall back to a warehouse-only lookup — otherwise the URL becomes
    // `by-commodity//by-warehouse/…` (empty segment) and 404s, so no stacks ever load.
    const source$: any = commodityUuid
      ? this.stackService.indexByCommodityUuidAndWarehouseUuid(commodityUuid, warehouseUuid)
      : this.stackService.indexByWarehouse(
          warehouseUuid,
          new PaginationOptions({ perPage: 100000, sort: 'stack_number', direction: 'asc' }, 'stack_input_stacks')
        );

    source$.subscribe({
      next: (res: any) => {
        // by-commodity returns an array; by-warehouse returns a paginated result — handle both.
        const rows: Array<any> = Array.isArray(res) ? res : (res?.data ?? []);
        this.stacks = rows.map((el) => new Stack(el));
        this.stacksList = rows.map((el) => {
          return {
            name: `${el.stack_number}`,
            value: el.uuid,
          };
        });
        this.writeValue(this.value);
        this.loading = false;
      },
      error: (error: any) => {
        console.error('Error loading stacks for filters:', error);
        this.clearStacks();
        this.loading = false;
      },
    });
  }

  private clearSelection(): void {
    this.value = undefined;
    this.localValue = undefined;
    this.onChange(undefined);
  }

  private clearStacks(): void {
    this.stacks = [];
    this.stacksList = [];
  }

  onChange = (val: string | undefined) => {
  };

  getFilteredList() {
    // No need to filter anymore since we're only loading stacks for the selected filters
    return this.stacksList;
  }


  onTouched = () => { };

  touched = false;
  disabled = false;

  writeValue(val?: string) {
    this.value = val;
    if (this.value != undefined || this.value != null) {
      this.localValue = this.stacksList.filter((el) => el.value == val)[0];
    }
  }

  onValueChange(val?: CustomSelectOption) {
    this.value = val?.value;
    this.onChange(this.value);
    this.markAsTouched();
  }


  registerOnChange(onChange: any) {
    this.onChange = onChange;
  }

  registerOnTouched(onTouched: any) {
    this.onTouched = onTouched;
  }

  markAsTouched() {
    if (!this.touched) {
      this.onTouched();
      this.touched = true;
    }
  }

  setDisabledState(disabled: boolean) {
    this.disabled = disabled;
  }

  validate(control: AbstractControl): ValidationErrors | null {
    const value = control.value;

    if (!value) {
      return null;
    }

    return null;
  }

  getTitle(): string {
    if (!this.filterCommodityUuid && !this.filterWarehouseUuid) {
      return "Please select commodity or warehouse first";
    }
    if (this.loading) {
      return "Loading stacks...";
    }
    if (this.stacksList.length === 0) {
      return "No stacks available for selected filters";
    }
    return this.title;
  }
}
